import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDate, getSyncLogs } from "@/lib/api";
import { Loader2 } from "lucide-react";

function StatusChip({ status }) {
  const map = {
    success: "text-[#16A34A] border-[#16A34A]/40 bg-[#16A34A]/5",
    failed: "text-[#DC2626] border-[#DC2626]/40 bg-[#DC2626]/5",
    partial: "text-[#CA8A04] border-[#CA8A04]/40 bg-[#CA8A04]/5",
    running: "text-[#007AFF] border-[#007AFF]/40 bg-[#007AFF]/5",
  };
  const cls = map[status] || "text-black/55 border-black/25";
  return (
    <span
      className={`inline-flex items-center border px-2 py-0.5 font-mono-plex text-[9px] uppercase tracking-[0.15em] ${cls}`}
    >
      {status || "unknown"}
    </span>
  );
}

export default function SyncLogsDialog({ open, onOpenChange }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getSyncLogs()
      .then((d) => setLogs(d.logs || []))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="sync-logs-dialog"
        className="rounded-none bg-white border border-black/15 text-black sm:max-w-3xl p-0 max-h-[80vh] flex flex-col"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-black/10 space-y-1">
          <DialogTitle className="font-heading text-xl font-light tracking-tight text-black">
            Sync logs
          </DialogTitle>
          <DialogDescription className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/55">
            most recent sync executions and error messages
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="py-16 text-center">
              <Loader2 className="w-5 h-5 animate-spin inline-block text-black/40" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-16 text-center font-mono-plex text-xs uppercase tracking-[0.2em] text-black/40">
              no sync runs yet
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-black/10">
                <tr>
                  {["Status", "Started", "Completed", "Fetched", "Inserted", "Updated", "Error"].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-4 font-mono-plex text-[10px] font-bold uppercase tracking-[0.2em] text-black/55"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={i} data-testid={`sync-log-row-${i}`} className="border-b border-black/5">
                    <td className="py-3 px-4"><StatusChip status={l.status} /></td>
                    <td className="py-3 px-4 font-mono-plex text-xs text-black/80 whitespace-nowrap">
                      {formatDate(l.started_at)}
                    </td>
                    <td className="py-3 px-4 font-mono-plex text-xs text-black/80 whitespace-nowrap">
                      {l.completed_at ? formatDate(l.completed_at) : "—"}
                    </td>
                    <td className="py-3 px-4 font-mono-plex text-xs text-black/80">{l.records_fetched}</td>
                    <td className="py-3 px-4 font-mono-plex text-xs text-black/80">{l.records_inserted}</td>
                    <td className="py-3 px-4 font-mono-plex text-xs text-black/80">{l.records_updated}</td>
                    <td className="py-3 px-4 font-mono-plex text-[10px] text-[#DC2626] max-w-xs truncate" title={l.error_message || ""}>
                      {l.error_message || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
